import {
  addMonths,
  addYears,
  endOfMonth,
  endOfYear,
  format,
  startOfMonth,
  startOfYear
} from 'date-fns';

const FROM_NOW_ID = 999;

export const sortFiltersAlphabetically = (a, b) => a.name.localeCompare(b.name);

export const getYearFilters = (allTime = false, delayed, type) => {
  const year = new Date().getFullYear();
  const filters = [
    {
      id: 0,
      name: `${delayed ? 'Award' : 'Current'} year ${year}`,
      type,
    },
    {
      id: 1,
      name: `${delayed ? 'Award' : 'Current'} year ${year - 1}`,
      type,
    },
    { id: 2, name: 'Last 3 years', type },
    { id: 3, name: 'Last 5 years', type },
  ];
  if (!allTime) {
    return filters;
  }
  return filters.concat({ id: 4, name: 'All winners since 1954', type });
};

export const getSIPYearFilters = (allTime = false) => {
  const year = new Date().getFullYear();
  const filters = [
    {
      id: 0,
      name: `Projects ${year}`,
      type: 'sip',
    },
    {
      id: 1,
      name: `Projects ${year - 1}`,
      type: 'sip',
    },
    { id: 2, name: 'Last 3 years', type: 'sip' },
    { id: 3, name: 'Last 5 years', type: 'sip' },
  ];
  if (!allTime) {
    return filters;
  }
  return filters.concat({ id: 4, name: 'All projects', type: 'sip' });
};

// Customer requires to advance to new year 20 days before
const yearForJuror = 2024;

export const getJurorYearFilters = () => [
  {
    id: 0,
    name: `Current year ${yearForJuror}`,
    type: 'juror',
    isJuror: true,
  },
  {
    id: 1,
    name: `Last year ${yearForJuror - 1}`,
    type: 'juror',
    isJuror: true,
  },
  { id: 2, name: 'All jurors since 1954', type: 'juror', isJuror: true },
];

export const mappedJurorFilterDates = {
  0: [yearForJuror],
  1: [yearForJuror - 1],
  2: Array.from(new Array(yearForJuror - 1953)).map(
    (_, index) => yearForJuror - index
  ),
};

export const getMappedFilterDates = () => {
  const year = new Date().getFullYear();
  return {
    0: [year],
    1: [year - 1],
    2: Array.from(new Array(3)).map((_, index) => year - index),
    3: Array.from(new Array(5)).map((_, index) => year - index),
    4: Array.from(new Array(year - 1953)).map((_, index) => year - index),
  };
};

const formatFutureDateForAPI = (date) => format(date, 'yyyy-MM-dd');

export const getMappedFilterFutureDates = (key) => {
  const map = {
    0: {
      fromDate: formatFutureDateForAPI(new Date()),
      toDate: formatFutureDateForAPI(endOfMonth(new Date())),
    },
    1: {
      fromDate: formatFutureDateForAPI(startOfMonth(addMonths(new Date(), 1))),
      toDate: formatFutureDateForAPI(endOfMonth(addMonths(new Date(), 1))),
    },
    2: {
      fromDate: formatFutureDateForAPI(startOfMonth(addMonths(new Date(), 1))),
      toDate: formatFutureDateForAPI(endOfMonth(addMonths(new Date(), 3))),
    },
    3: {
      fromDate: formatFutureDateForAPI(new Date()),
      toDate: formatFutureDateForAPI(endOfYear(new Date())),
    },
    4: {
      fromDate: formatFutureDateForAPI(startOfYear(addYears(new Date(), 1))),
      toDate: formatFutureDateForAPI(endOfYear(addYears(new Date(), 1))),
    },
    [FROM_NOW_ID]: {
      fromDate: formatFutureDateForAPI(new Date()),
      toDate: formatFutureDateForAPI(endOfYear(addYears(new Date(), 1))),
    },
  };

  return map[key || FROM_NOW_ID];
};

export const getMonthFilters = (type) => {
  const filters = [
    {
      id: 0,
      name: `This month`,
      type,
    },
    {
      id: 1,
      name: `Next month`,
      type,
    },
    { id: 2, name: 'Next 3 months', type },
    { id: 3, name: 'This year', type },
    { id: 4, name: 'Next year', type },
  ];

  return filters;
};

export const getActiveFilters = (filtersMap, queryFilters) => {
  if (!filtersMap || !queryFilters) {
    return {};
  }

  const activeFilters = Object.fromEntries(
    Object.entries(filtersMap).map(([key, filters]) => [
      getFilterKeyQueryParamKey(key),
      filters.filter((filter) => queryFilters[key]?.includes(filter.id)),
    ])
  );

  activeFilters.find = queryFilters['find'];
  activeFilters.profile = queryFilters['profile'];
  activeFilters.inspiration = queryFilters['inspiration'];
  activeFilters.sort = queryFilters['sort'];
  activeFilters.dynamicFilterId = queryFilters['dynamicFilterId'];

  return activeFilters;
};

const filterKeyQueryParamKeyMap = {
  awards: 'awardId',
  profileTypes: 'profileTypeId',
  time: 'yearId',
  region: 'regionId',
  countries: 'countryId',
  country: 'countryId',
  fieldsOfWork: 'fieldOfWorkId',
  sectors: 'sectorId',
  disciplines: 'disciplineId',
  categories: 'categoryId',
  articleType: 'articleTypeId',
  readingTime: 'readingTimeId',
  pressType: 'pressTypeId',
  eventType: 'eventTypeId',
};

export const queryParamKeyMicrocopyKeyMap = {
  awardId: 'awards',
  profileTypeId: 'profileTypes',
  yearId: 'time',
  regionId: 'region',
  countryId: 'country',
  fieldOfWorkId: 'fieldsOfWork',
  sectorId: 'sectors',
  disciplineId: 'discipline',
  categoryId: 'category',
  articleTypeId: 'articleType',
  readingTimeId: 'readingTime',
  pressTypeId: 'pressType',
  eventTypeId: 'eventType',
};

const getFilterKeyQueryParamKey = (filterKey) => {
  return filterKeyQueryParamKeyMap[filterKey] || filterKey;
};

// builds new search params from current or provided search params, and previous filter state
export const buildSearchParams = ({
  filterKey,
  filter,
  isSingleValue = false,
  baseSearchParams = null,
}) => {
  let searchParams = baseSearchParams || new URLSearchParams(location.search);

  const key = getFilterKeyQueryParamKey(filterKey);
  if (isSingleValue) {
    if (filter.checked) {
      searchParams.delete(key);
    } else {
      searchParams.set(key, filter.id);
    }
  } else {
    const pairs = Array.from(searchParams.entries());
    if (filter.checked) {
      searchParams = new URLSearchParams(
        pairs.filter(([pairKey, pairVal]) => {
          if (pairKey === key && pairVal === String(filter.id)) {
            return false;
          }
          return true;
        })
      );
    } else {
      searchParams.append(key, filter.id);
    }
  }

  searchParams.sort();

  return searchParams;
};

export const buildSearchParamsFromActiveFiltersIdsMap = (filters) => {
  const searchParams = new URLSearchParams();

  for (let [filterKey, filterValue] of Object.entries(filters)) {
    if (filterValue !== undefined) {
      if (Array.isArray(filterValue)) {
        for (let el of filterValue) {
          searchParams.append(getFilterKeyQueryParamKey(filterKey), el);
        }
      } else {
        searchParams.set(getFilterKeyQueryParamKey(filterKey), filterValue);
      }
    }
  }

  searchParams.sort();

  return searchParams;
};

// remove from query params anything that is not active
export const cleanSearchParams = (filters) => {
  const activeFilterIdsMap = getActiveFilterIdsMap(filters);

  const params = buildSearchParamsFromActiveFiltersIdsMap(activeFilterIdsMap);

  if (history.pushState) {
    var newurl =
      window.location.protocol +
      '//' +
      window.location.host +
      window.location.pathname +
      `?${params.toString()}`;
    window.history.replaceState({ path: newurl }, '', newurl);
  }
};

const getActiveFilterIdsMap = (filters) => {
  const entries = Object.entries(filters).map(([key, val]) => {
    if (Array.isArray(val)) {
      return [key, val.map((el) => el.id)];
    }
    return [key, val];
  });

  return Object.fromEntries(entries);
};

export const getFiltersFromQueryParams = (
  defaultFilters,
  queryParams,
  mapping = {},
  customResolvers = {}
) => {
  const isInteger = (str) => {
    if (typeof str !== 'string') {
      return false;
    }

    const num = Number(str);

    if (Number.isInteger(num)) {
      return true;
    }

    return false;
  };

  const filters = { ...defaultFilters };
  const keyValuePairs = queryParams.split('&');

  keyValuePairs.forEach((pair) => {
    const key = pair.split('=').shift();
    let val;
    if (key === 'profile') {
      val = pair.split('|id=').pop();
    } else {
      val = pair.split('=').pop();
    }
    const value = isInteger(val) ? Number(val) : val;
    const filterKey = mapping[key] || key.split('Id').shift();
    const valueToAdd = customResolvers[key]?.(value) || value;

    if (filters[filterKey] && Array.isArray(filters[filterKey])) {
      filters[filterKey] = filters[filterKey].concat(valueToAdd);
    } else {
      filters[filterKey] = valueToAdd;
    }
  });

  return filters;
};

export const getOpenedFilters = (filters, defaults = []) => {
  const activeFilters = Object.entries(filters)
    .filter(([_, val]) => Array.isArray(val) && val.length)
    .map(([key, _]) => key);

  return [...defaults, ...activeFilters];
};
